import React, { useState, useEffect } from 'react';
import { CodeBody, Title, Subtitle } from '../styledComponents';
import { TextButton, BUTTON_SIZES } from 'cdk-radial';
import CodeHighlight from './CodeHighlight';
import SchemaStructure from './SchemaStructure';
import { ResponseSelection } from './ResponseSelection';
import { BODY_TYPES } from './constants';
import { getPropertyType } from '../../../utils/spec-utils';
import { formatExample } from '../../../utils/format-data';
import { AttributeSelection } from "../AttributeSelection";

import '../api-explorer.css';

export const ResponseBodySection = ({ endpoint, schema, belongsTo }) => {
  const [responseCodes, setResponseCodes] = useState([]);
  const [schemaResponse, setSchemaResponse] = useState({});
  const [responseBodyExample, setResponseBodyExample] = useState({});
  const [activeHttpCode, setActiveHttpCode] = useState();
  const [responseStructs, setResponseStructs] = useState([]);
  const [selectedExample, setSelectedExample] = useState();
  const [responseExamples, setResponseExamples] = useState([]);

  const isValidJson = input => {
    try {
      JSON.parse(input);
    } catch (e) {
      return false;
    }
    return true;
  };

  const getResponseExample = (endpoint, activeHttpCode) => {
    let responseExample;
    const parent = endpoint.schema?.responses[activeHttpCode]?.content
      ? Object.keys(endpoint.schema.responses[activeHttpCode]?.content)[0]
      : [];

    const schemaResponse = endpoint
      ? endpoint?.schema?.responses[activeHttpCode] &&
        !!parent &&
        endpoint?.schema?.responses[activeHttpCode]?.content?.[parent]?.schema
      : null;
    
    if (!!schemaResponse) {
      responseExample =
        endpoint.schema.responses[activeHttpCode]?.content?.[parent]?.schema
          .example;
    }
    // OAS3 examples, take parent example over schema
    const schemaResponseContent = endpoint?.schema?.responses[activeHttpCode]?.content;
    const parentExample = schemaResponseContent?.[parent]?.example || schemaResponseContent?.[parent]?.examples;
    if (!!parentExample) {
      responseExample = parentExample;
    }
    if (typeof responseExample === 'string' && isValidJson(responseExample)) {
      responseExample = JSON.parse(responseExample);
    }
    // Handle multiple response examples
    if (typeof responseExample === 'object') {      
      const responseEntries = Object.entries(responseExample);
      // Check that every entry has a "value" property
      const everyEntryHasValue = responseEntries.map(e => e[1]).every(e => Object.keys(e || {})[0] === 'value');
      if (everyEntryHasValue) {
        // Remove the "value" property from the response entries
        const availableResponseExamples = Object.fromEntries(responseEntries.map(e => [e[0], e[1].value]));
        setResponseExamples(availableResponseExamples);
        const firstExample = Object.keys(availableResponseExamples)[0];
        setSelectedExample(firstExample);
        responseExample = availableResponseExamples[firstExample];
      }
    }

    responseExample =
      !!schemaResponse && !responseExample
        ? getPropertyType(schemaResponse)
        : responseExample;
    return responseExample;
  };

  useEffect(() => {
    if (!!endpoint?.schema?.responses) {
      const responseCodes = Object.keys(endpoint.schema?.responses);
      setResponseCodes(responseCodes);
      const leastCode = responseCodes.reduce((acc, loc) =>
        acc < loc ? acc : loc
      );
      setActiveHttpCode(leastCode);
    }
  }, [endpoint?.schema?.responses]);

  useEffect(() => {
    const isOas3Spec = !!schema?.openapi && schema.openapi.indexOf('3') > -1;
    // clear out the multiple response examples
    setResponseExamples();

    if (isOas3Spec) {
      const parent = endpoint.schema?.responses[activeHttpCode]?.content
        ? Object.keys(endpoint.schema.responses[activeHttpCode]?.content)[0]
        : [];

      const schemaResponse = endpoint
        ? endpoint.schema.responses[activeHttpCode] &&
          !!parent &&
          endpoint.schema.responses[activeHttpCode]?.content?.[parent]?.schema
        : null;

      setSchemaResponse(schemaResponse);
      setResponseStructure(schemaResponse);

      const responseExample = getResponseExample(endpoint, activeHttpCode);
      setResponseBodyExample(responseExample);
    } else {
      const schemaResponse = endpoint
        ? endpoint?.schema?.responses[activeHttpCode] &&
          endpoint?.schema?.responses[activeHttpCode]?.schema
        : null;

      const responseBodyExample = endpoint
        ? schemaResponse &&
          endpoint?.schema?.responses[activeHttpCode]?.schema?.example
        : null;
      setSchemaResponse(schemaResponse);
      setResponseBodyExample(responseBodyExample);
    }
  }, [activeHttpCode, endpoint]);

  const setResponseStructure = schemaResponse => {
    let respStruct = [];
    if (
      !!schemaResponse &&
      Object.keys(schemaResponse).length > 0 &&
      !!schemaResponse?.oneOf
    ) {
      schemaResponse.oneOf.forEach((f, idx) => {
        respStruct = [...respStruct, { id: idx, schema: f }];
      });
    }
    setResponseStructs(respStruct);
  };

  const handleSelectedResponse = resp => {
    const locResponse = responseStructs.find(f => f.id === resp)?.schema;
    setSchemaResponse(locResponse);
    setResponseBodyExample(locResponse?.example);
  };

  const handleSelectedExample = (attrKey, example) => {
    if (attrKey === 'exs') {
      setSelectedExample(example.label);
      setResponseBodyExample(responseExamples[example.label]);
    }
  };

  return (
    <>
      <Title center>Response</Title>
      <div className="api-dir-pkg_response-code-wrapper">
        {responseCodes.map(m => (
          <div
            className={`api-dir-pkg_status-code-wrapper ${
              activeHttpCode === m ? 'api-dir-pkg_status-code-selected' : ''
            }`}
          >
            <TextButton
              size={BUTTON_SIZES.SMALL}
              text={m}
              onClick={() => setActiveHttpCode(m)}
            />
          </div>
        ))}
      </div>

      <ResponseSelection
        activeHttpCode={activeHttpCode}
        responseStructs={responseStructs}
        setSelectedResponse={handleSelectedResponse}
      />
      {!!schemaResponse && (
        <>
          <Subtitle>Response Body Structure</Subtitle>
          <SchemaStructure
            structure={'Response'}
            schema={schemaResponse}
            type={BODY_TYPES.response}
            belongsTo={belongsTo}
          />
        </>
      )}
      {responseBodyExample && (
        <>
          <Subtitle>Response Body Example</Subtitle>
          {responseExamples && Object.keys(responseExamples).length > 0 && (
            <AttributeSelection
              dataTestId="examples-select"
              key="examples-drop-down"
              id="examples-drop-down"
              attributeKey="exs"
              items={Object.keys(responseExamples)}
              onItemSelection={handleSelectedExample}
              defaultOption={selectedExample}
            />
          )}
          <CodeBody data-cy={`apiexplorer-responsecode-body`}>
            <CodeHighlight language={`${typeof responseBodyExample === 'object' ? 'json': 'markup'}`}>
              {formatExample(responseBodyExample)}
            </CodeHighlight>
          </CodeBody>
        </>
      )}
    </>
  );
};
